import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, InstagramLogo, ArrowsClockwise, CheckCircle, WarningCircle, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";

const ASPECTS = [
    { key: "1:1",    label: "Square",    w: 1080, h: 1080 },
    { key: "4:5",    label: "Portrait",  w: 1080, h: 1350 },
    { key: "1.91:1", label: "Landscape", w: 1080, h: 566 },
];

function fmtDate(iso) {
    if (!iso) return null;
    try {
        return new Date(iso).toLocaleString("en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return null; }
}

const captionHashtags = (tags) => {
    const seen = new Set();
    const hs = (tags || [])
        .map(t => String(t).split(/\s+/).map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("").replace(/[^A-Za-z0-9_]/g, ""))
        .filter(h => h && !seen.has(h.toLowerCase()) && seen.add(h.toLowerCase()));
    return hs.length ? `\n\n${hs.map(h => `#${h}`).join(" ")}` : "";
};

export default function SocialManager() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [posts, setPosts] = useState([]);
    const [insta, setInsta] = useState({ connected: false, configured: false });
    const [selectedId, setSelectedId] = useState(null);
    const [aspect, setAspect] = useState("4:5");
    const [preview, setPreview] = useState(null);
    const [previewBusy, setPreviewBusy] = useState(false);
    const [publishBusy, setPublishBusy] = useState(false);
    const [search, setSearch] = useState("");

    useEffect(() => {
        if (!loading && !user?.is_admin) navigate("/login");
    }, [loading, user, navigate]);

    const loadAll = useCallback(async () => {
        try {
            const [postsRes, instaRes] = await Promise.all([
                api.get("/admin/posts"),
                api.get("/admin/instagram/status"),
            ]);
            const published = postsRes.data.filter((p) => p.published && p.cover_image?.startsWith("https://"));
            setPosts(published);
            setInsta(instaRes.data || {});
            if (!selectedId && published.length) setSelectedId(published[0].id);
        } catch {
            toast.error("Could not load social manager");
        }
    }, [selectedId]);

    useEffect(() => { loadAll(); }, [loadAll]);

    const selected = useMemo(() => posts.find((p) => p.id === selectedId) || null, [posts, selectedId]);

    const loadPreview = useCallback(async () => {
        if (!selected) { setPreview(null); return; }
        setPreviewBusy(true);
        setPreview(null);
        try {
            const { data } = await api.post(`/admin/posts/${selected.id}/preview-social-card`, { aspect });
            setPreview(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not render preview");
        } finally {
            setPreviewBusy(false);
        }
    }, [selected, aspect]);

    useEffect(() => { loadPreview(); }, [loadPreview]);

    const publish = async () => {
        if (!selected) return;
        if (!insta.connected) { toast.error("Instagram is not connected"); return; }
        if (selected.instagram_media_id && !window.confirm("Already posted to Instagram. Publish again?")) return;

        setPublishBusy(true);
        const t = toast.loading("Publishing to Instagram… this can take up to a minute.");
        try {
            await api.post(`/admin/posts/${selected.id}/share-instagram`, { aspect });
            setPosts((prev) => prev.map((p) => p.id === selected.id ? { ...p, instagram_share_status: "pending", instagram_share_error: null } : p));
        } catch (e) {
            toast.dismiss(t);
            toast.error(e?.response?.data?.detail || "Instagram publish failed");
            setPublishBusy(false);
            return;
        }

        // Poll until the share finishes or 3 minutes elapse.
        const started = Date.now();
        const tick = async () => {
            try {
                const { data } = await api.get("/admin/posts");
                const published = data.filter((p) => p.published && p.cover_image?.startsWith("https://"));
                setPosts(published);
                const u = published.find((p) => p.id === selected.id);
                const st = u?.instagram_share_status;
                if (st === "success") {
                    toast.dismiss(t);
                    toast.success(`Posted to Instagram (id ${u.instagram_media_id})`);
                    setPublishBusy(false);
                    return;
                }
                if (st === "failed") {
                    toast.dismiss(t);
                    toast.error(u?.instagram_share_error || "Instagram publish failed");
                    setPublishBusy(false);
                    return;
                }
            } catch { /* transient */ }
            if (Date.now() - started > 180_000) {
                toast.dismiss(t);
                toast.error("Still publishing — check back in a minute.");
                setPublishBusy(false);
                return;
            }
            setTimeout(tick, 3000);
        };
        setTimeout(tick, 3000);
    };

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return posts.filter((p) => !q || p.title?.toLowerCase().includes(q) || p.category?.toLowerCase().includes(q));
    }, [posts, search]);

    if (loading || !user?.is_admin) return null;

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="border-b border-zinc-900 sticky top-0 bg-black/90 backdrop-blur z-10">
                <div className="max-w-[1400px] mx-auto px-6 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/admin" className="text-zinc-400 hover:text-white transition-colors inline-flex items-center gap-2" data-testid="social-back-link">
                            <ArrowLeft size={18} weight="bold" /> Admin
                        </Link>
                        <div className="h-6 w-px bg-zinc-800" />
                        <h1 className="font-display text-2xl tracking-tight flex items-center gap-3">
                            <InstagramLogo size={22} weight="fill" className="text-[#FFD000]" />
                            Social Post Manager
                        </h1>
                    </div>
                    <div className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.25em]" data-testid="social-instagram-status">
                        {insta.connected ? (
                            <span className="text-emerald-300 inline-flex items-center gap-2">
                                <CheckCircle size={14} weight="fill" /> IG: @{insta.username}
                            </span>
                        ) : (
                            <span className="text-red-300 inline-flex items-center gap-2">
                                <WarningCircle size={14} weight="fill" /> IG not connected
                            </span>
                        )}
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto grid grid-cols-12 gap-6 p-6">
                {/* Left: post picker */}
                <aside className="col-span-4 lg:col-span-3 space-y-3">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search articles…"
                        data-testid="social-search"
                        className="w-full bg-black border border-zinc-800 focus:border-[#FFD000] focus:outline-none px-3 py-2 text-sm"
                    />
                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 px-1">
                        Published · {filtered.length}
                    </div>
                    <div className="border border-zinc-900 divide-y divide-zinc-900 max-h-[calc(100vh-220px)] overflow-y-auto" data-testid="social-post-list">
                        {filtered.map((p) => {
                            const active = p.id === selectedId;
                            const shared = !!p.instagram_media_id;
                            const publishing = p.instagram_share_status === "pending";
                            const failed = !!p.instagram_share_error && !shared;
                            return (
                                <button
                                    key={p.id}
                                    onClick={() => setSelectedId(p.id)}
                                    className={`w-full text-left p-3 flex gap-3 items-start hover:bg-zinc-900/50 transition-colors ${active ? "bg-zinc-900/70 border-l-2 border-[#FFD000]" : ""}`}
                                    data-testid={`social-post-${p.id}`}
                                >
                                    <img src={p.cover_image} alt="" className="w-14 h-14 object-cover flex-shrink-0" loading="lazy" />
                                    <div className="min-w-0 flex-1">
                                        <div className="text-sm font-medium text-white line-clamp-2 leading-tight">{p.title}</div>
                                        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mt-1 flex items-center gap-2 flex-wrap">
                                            <span>{p.category}</span>
                                            {publishing && <span className="text-[#FFD000] animate-pulse">· Publishing</span>}
                                            {shared && !publishing && <span className="text-emerald-400">· Shared ✓</span>}
                                            {failed && !publishing && <span className="text-red-400">· Failed</span>}
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                        {filtered.length === 0 && (
                            <div className="p-6 text-center text-sm text-zinc-500">No published posts match.</div>
                        )}
                    </div>
                </aside>

                {/* Right: preview + controls */}
                <main className="col-span-8 lg:col-span-9">
                    {!selected ? (
                        <div className="border border-zinc-900 p-12 text-center text-zinc-500">
                            Pick a post from the list to preview its Instagram card.
                        </div>
                    ) : (
                        <div className="grid grid-cols-12 gap-6">
                            {/* Card preview */}
                            <div className="col-span-12 md:col-span-7">
                                <div className="border border-zinc-900 bg-zinc-950 p-6 flex items-center justify-center min-h-[400px]" data-testid="social-preview-panel">
                                    {previewBusy ? (
                                        <div className="text-zinc-500 flex flex-col items-center gap-3">
                                            <ArrowsClockwise size={22} className="animate-spin text-[#FFD000]" />
                                            <span className="text-xs font-mono uppercase tracking-[0.25em]">Rendering card…</span>
                                        </div>
                                    ) : preview?.url ? (
                                        <img
                                            src={preview.url}
                                            alt="Social card preview"
                                            className="max-h-[70vh] max-w-full object-contain shadow-2xl"
                                            data-testid="social-preview-image"
                                            key={preview.url}
                                        />
                                    ) : (
                                        <div className="text-zinc-500 text-sm">Preview will appear here.</div>
                                    )}
                                </div>
                                {preview && (
                                    <div className="mt-2 text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 flex items-center justify-between">
                                        <span>{preview.width} × {preview.height} px · {preview.label}</span>
                                        <a href={preview.url} target="_blank" rel="noreferrer" className="hover:text-[#FFD000] inline-flex items-center gap-1">
                                            Open <ArrowSquareOut size={11} />
                                        </a>
                                    </div>
                                )}
                            </div>

                            {/* Controls */}
                            <div className="col-span-12 md:col-span-5 space-y-6">
                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">Article</div>
                                    <div className="text-lg font-display leading-tight" data-testid="social-selected-title">{selected.title}</div>
                                    <div className="mt-1 text-xs text-zinc-500 flex items-center gap-3">
                                        <span className="font-mono uppercase tracking-[0.2em]">{selected.category}</span>
                                        <span>·</span>
                                        <span>{fmtDate(selected.created_at)}</span>
                                    </div>
                                </div>

                                <div>
                                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500 mb-2">Aspect ratio</div>
                                    <div className="grid grid-cols-3 gap-2" data-testid="social-aspect-picker">
                                        {ASPECTS.map((a) => (
                                            <button
                                                key={a.key}
                                                onClick={() => setAspect(a.key)}
                                                className={`border p-3 text-left transition-colors ${aspect === a.key ? "border-[#FFD000] bg-[#FFD000]/5" : "border-zinc-800 hover:border-zinc-700"}`}
                                                data-testid={`social-aspect-${a.key}`}
                                            >
                                                <div className={`text-sm font-medium ${aspect === a.key ? "text-[#FFD000]" : "text-white"}`}>{a.label}</div>
                                                <div className="text-[10px] font-mono uppercase tracking-[0.15em] text-zinc-500 mt-1">{a.key}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="border border-zinc-900 p-4 space-y-2">
                                    <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-zinc-500">Instagram caption</div>
                                    <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line" data-testid="social-caption-preview">
                                        {selected.excerpt ? `${selected.excerpt.trim()}\n\n` : ""}To read the full story, head over to our website (link in bio){captionHashtags(selected.tags)}
                                    </p>
                                </div>

                                <div className="space-y-2 text-xs text-zinc-400">
                                    {selected.instagram_media_id && (
                                        <div className="text-emerald-400 inline-flex items-center gap-2">
                                            <CheckCircle size={14} weight="fill" />
                                            Already shared · {fmtDate(selected.instagram_shared_at)}
                                        </div>
                                    )}
                                    {selected.instagram_share_status === "pending" && (
                                        <div className="text-[#FFD000] inline-flex items-center gap-2 animate-pulse">
                                            <ArrowsClockwise size={14} className="animate-spin" />
                                            Publishing to Instagram…
                                        </div>
                                    )}
                                    {selected.instagram_share_error && !selected.instagram_media_id && selected.instagram_share_status !== "pending" && (
                                        <div className="text-red-400 flex items-start gap-2">
                                            <WarningCircle size={14} weight="fill" className="mt-0.5 flex-shrink-0" />
                                            <span className="break-all">{selected.instagram_share_error}</span>
                                        </div>
                                    )}
                                </div>

                                <div className="flex flex-col gap-2">
                                    <button
                                        onClick={publish}
                                        disabled={publishBusy || previewBusy || !insta.connected || selected.instagram_share_status === "pending"}
                                        className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed py-3"
                                        data-testid="social-publish-button"
                                    >
                                        <InstagramLogo size={16} weight="bold" />
                                        {selected.instagram_media_id ? "Publish again" : "Publish to Instagram"}
                                    </button>
                                    <button
                                        onClick={loadPreview}
                                        disabled={previewBusy}
                                        className="btn-secondary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50 text-xs"
                                        data-testid="social-regenerate-button"
                                    >
                                        <ArrowsClockwise size={14} className={previewBusy ? "animate-spin" : ""} />
                                        Regenerate preview
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </main>
            </div>
        </div>
    );
}
